import { InitialResolver } from "@/lib/use.middlewares";
import { NewAddListingForm } from "@/components/add-v2/form/newForm";
import React, { useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_ADD_LISTING_CONFIG } from "@/graphql/queries/listing";
import { useAuthState } from "@/contexts/auth/auth.provider";
import css from "./.module.scss";
import { useRouter } from "next/router";
import Head from "next/head";
import logger from "@/console";
import withAuthentication from "@/hoc/withAuthentication";

export function useFetchCategoryFields() {
  const { token } = useAuthState();
  const [fetched, setFetched] = useState(false);
  const [getAddListingConfig] = useLazyQuery(GET_ADD_LISTING_CONFIG, {
    context: { token },
  });

  return [
    (category) => {
      const controller = new AbortController();

      const fetcher = getAddListingConfig({
        variables: {
          category,
        },
      })
        .then(({ data }) => {
          return fetch(`/next-api/fields/add?category=${category}`, {
            signal: controller.signal,
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              fields:
                data?.Listing?.getAddingListingConfig.category_fields?.fields,
            }),
          });
        })
        .then((res) => res.json())
        .then((data) => {
          setFetched(true);
          return data;
        });

      return [
        fetcher,
        () => {
          try {
            controller?.abort("No more data");
          } catch {}
        },
      ];
    },
    fetched,
  ];
}
export const AddMarketingRequestStepsPage = withAuthentication(
  function AddMarketingRequestStepsPage({ query, ...p } = {}) {
    const router = useRouter();

    const step = router.query.step;
    const stepRefMap = useRef({});
    const [getAddListingConfig, fetched] = useFetchCategoryFields();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    useEffect(() => {
      const ctx =
        JSON.parse(localStorage.getItem("newListingDraft") || "{}") || {};
      if (!ctx?.category) {
        router.replace("/add-listing/category");

        return;
      }
      const [fetcher, abort] = getAddListingConfig(ctx.category);
      fetcher
        .then((data) => {
          const { steps } = data;

          setData({
            ...data,
            initial: {
              ...data.initial,
              ...ctx,
            },
          });
          stepRefMap.current = steps.reduce(
            (acc, step, index) => ({
              ...acc,
              [step.name]: index,
            }),
            {},
          );
          stepRefMap.current__steps__ = steps;
        })
        .catch(logger.log)
        .finally(() => {
          setLoading(false);
        });
      return () => {
        abort();
      };
    }, []);

    useEffect(
      function validateCurrentUrlStep() {
        const ctx = JSON.parse(localStorage.getItem("newListingDraft") || "{}");
        if (!ctx.category || ctx?.category > 100) {
          router.replace("/add-listing/category");
          return;
        }
        if (!fetched) return;
        let st = stepRefMap.current[step];

        if (
          st === undefined &&
          stepRefMap.current[localStorage.getItem("addListingStep")] !==
            undefined
        ) {
          router.replace(
            "/add-listing/" + localStorage.getItem("addListingStep"),
          );
          return;
        }

        NewAddListingForm.setStep(st);

        const hasPrice = data.steps.some(
          (step, index) =>
            step.name === "general" &&
            data.steps[index].fields.some((field) => field.name === "price"),
        );
        if (
          hasPrice &&
          !ctx.price &&
          step !== "general" &&
          stepRefMap.current["general"] < st
        ) {
          router.replace("/add-listing/general");
          return;
        }

        if (
          !ctx.location &&
          step !== "location" &&
          stepRefMap.current["location"] < st
        ) {
          router.replace("/add-listing/general");
          return;
        }

        if (!stepRefMap.current[step])
          router.replace(`/add-listing/${stepRefMap.current__steps__[0].name}`);
      },
      [fetched, data, step],
    );

    return (
      <div className={css.root}>
        <Head>
          <title>إضافة طلب تسويق | عقار</title>
        </Head>
        <h1>إضافة طلب تسويق</h1>
        <NewAddListingForm
          {...data}
          loading={loading}
          onFinish={(values) => {
            localStorage.setItem("newListingDraft", JSON.stringify(values));
            router.push(`/add-listing/preview`);
          }}
          onStepChange={(stepIndex, { step, ctx }) => {
            localStorage.setItem("newListingDraft", JSON.stringify(ctx));
            localStorage.setItem("addListingStep", step.name);
            router.push(`/add-listing/${step.name}`, null, { scroll: true });
          }}
        />
        <div className={css.actions}>
          <NewAddListingForm.ActionButton
            name="previous"
            onClick={NewAddListingForm.previous}
          >
            السابق
          </NewAddListingForm.ActionButton>

          <NewAddListingForm.ActionButton
            name="next"
            className={css.next}
            theme="primary"
            onClick={NewAddListingForm.next}
          >
            التالي
          </NewAddListingForm.ActionButton>
        </div>
      </div>
    );
  },
);
AddMarketingRequestStepsPage.getInitialProps = InitialResolver(async (ctx) => {
  return { query: ctx.query };
});

export default AddMarketingRequestStepsPage;
